
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import TasksOverview from '@/components/tasks/TasksOverview';
import TaskFilters from '@/components/tasks/TaskFilters';
import { Kanban, ListTodo, Loader2, Plus } from 'lucide-react';
import { useTaskMutations } from '@/hooks/useTaskMutations';
import { useTaskFilters } from '@/hooks/useTaskFilters';
import { TaskWithLead } from '@/types/taskFilters';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import TaskUserFilter from '@/components/tasks/TaskUserFilter';
import { Task, TaskPriority, TaskStatus } from '@/types/lead';
import { TaskBoard } from '@/components/tasks/kanban/TaskBoard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getTaskPriorityOptions, getTaskCategoryOptions } from '@/services/leads/taskService';
import { useIsMobile } from '@/hooks/use-mobile';
import TaskCategoryFilter from '@/components/tasks/TaskCategoryFilter';
import { useLocation } from 'react-router-dom';
import AddTaskDialog from '@/components/tasks/AddTaskDialog';

type ViewMode = 'kanban' | 'list';

const Tasks = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const { completeMutation, deleteMutation, updateMutation } = useTaskMutations();
  const { data: companyMembers, isLoading: isLoadingMembers } = useCompanyMembers();
  const priorityOptions = getTaskPriorityOptions();
  const categoryOptions = getTaskCategoryOptions().map(option => ({
    label: option.label,
    value: option.value
  }));
  const isMobile = useIsMobile();
  const location = useLocation();
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  
  // Extract taskId from URL query parameters if present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const taskId = searchParams.get('taskId');
    if (taskId) {
      setHighlightedTaskId(taskId);
      // Force list view when a task is highlighted
      setViewMode('list');
    }
  }, [location.search]);
  
  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks', user?.id],
    queryFn: async () => {
      // Get user's company_id first
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.company_id) {
        console.warn('User has no company_id');
        return [];
      }
      
      // Fetch tasks where the associated lead belongs to the user's company
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          leads!inner (
            id,
            name,
            status,
            company_id
          ),
          assignee: profiles!tasks_assigned_to_fkey (
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('leads.company_id', profileData.company_id)
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false });
        
      if (error) throw error;

      console.log('Tasks Query Debug:', {
        totalTasks: data?.length || 0,
        userId: user?.id,
        companyId: profileData.company_id,
        firstTask: data?.[0],
      });

      return (data || []).map(item => ({
        id: item.id,
        leadId: item.lead_id,
        title: item.title,
        description: item.description,
        dueDate: item.due_date,
        priority: item.priority as TaskPriority,
        status: item.status as TaskStatus,
        assignedTo: item.assigned_to,
        createdAt: item.created_at,
        completedAt: item.completed_at,
        categories: item.categories || [],
        leads: item.leads ? {
          id: item.leads.id,
          business_name: item.leads.name,
          status: item.leads.status
        } : {
          id: item.lead_id,
          business_name: 'Unknown Lead',
          status: 'new'
        },
        assignee: item.assignee ? {
          first_name: item.assignee.first_name || '',
          last_name: item.assignee.last_name || '',
          avatar_url: item.assignee.avatar_url || null,
        } : undefined
      })) as TaskWithLead[];
    },
    enabled: !!user?.id,
  });

  const { filters, updateFilters, resetFilters, groupedTasks } = useTaskFilters(tasks || [], user?.id || '');

  // Get unique categories from all tasks
  const uniqueCategories = React.useMemo(() => {
    if (!tasks) return [];
    
    const categorySet = new Set<string>();
    tasks.forEach(task => {
      if (task.categories) {
        task.categories.forEach(cat => categorySet.add(cat));
      }
    });
    
    return Array.from(categorySet).map(category => ({
      label: category,
      value: category
    }));
  }, [tasks]);

  // Combine predefined and user-created categories
  const allCategories = React.useMemo(() => {
    const predefinedValues = categoryOptions.map(c => c.value);
    const uniqueUserCategories = uniqueCategories.filter(c => !predefinedValues.includes(c.value));
    return [...categoryOptions, ...uniqueUserCategories];
  }, [categoryOptions, uniqueCategories]);

  const handleCompleteTask = (taskId: string) => {
    completeMutation(taskId);
  };

  const handleDeleteTask = (taskId: string) => {
    deleteMutation(taskId);
  };

  const handleUpdateTask = (taskId: string, updates: Partial<Task>) => {
    updateMutation(taskId, updates);
  };

  const getPriorityBadge = (priority: string) => {
    const option = priorityOptions.find(opt => opt.value === priority);
    if (!option) return null;
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="w-full sm:w-auto">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Task Management</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              Organize and track your team's tasks
            </p>
          </div>
          
            <Button 
              onClick={() => setIsAddTaskDialogOpen(true)}
              className="gap-2 flex-shrink-0 w-full sm:w-auto"
            >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Task</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        <Card id="mobile-filters-section" className="bg-white shadow-sm border-gray-200 section-spacing scroll-mt-24">
          <CardContent className="p-4 sm:p-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 w-full">
                <TaskUserFilter
                  companyUsers={companyMembers || []}
                  selectedUserId={filters.userId}
                  onUserSelect={(userId) => updateFilters({ userId })}
                  isLoading={isLoadingMembers}
                />
                <TaskCategoryFilter
                  selectedCategories={filters.categories}
                  onCategoriesChange={(categories) => updateFilters({ categories })}
                  availableCategories={allCategories}
                />
                <TaskFilters 
                  filters={filters}
                  onFiltersChange={updateFilters}
                  onReset={resetFilters}
                />
              </div>
              
              <div className="flex sm:inline-flex w-full sm:w-auto items-center bg-gray-100 rounded-lg p-1 gap-1">
                <Button
                  variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                  className="h-9 px-3 flex-1 sm:flex-none justify-center"
                >
                  <Kanban className="h-4 w-4" />
                  {!isMobile && <span className="ml-2">Board</span>}
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-9 px-3 flex-1 sm:flex-none justify-center"
                >
                  <ListTodo className="h-4 w-4" />
                  {!isMobile && <span className="ml-2">List</span>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
        
      <Card className="bg-white shadow-md border-gray-200">
        <CardHeader className="border-b p-6">
          <CardTitle className="text-xl font-semibold">
            {viewMode === 'kanban' ? 'Task Board' : 'Task List'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {viewMode === 'kanban' ? (
            <div className="p-4 md:p-6">
              <TaskBoard
                pendingTasks={groupedTasks.pending}
                overdueTasks={groupedTasks.overdue}
                completedTasks={groupedTasks.completed}
                onUpdateTask={handleUpdateTask}
                onDeleteTask={handleDeleteTask}
                onCompleteTask={handleCompleteTask}
                getPriorityBadge={getPriorityBadge}
              />
            </div>
          ) : (
            <div className="space-y-4 p-4 md:p-6">
              <TasksOverview 
                tasks={groupedTasks.pending.concat(groupedTasks.overdue)}
                type="pending"
                onCompleteTask={handleCompleteTask}
                onDeleteTask={handleDeleteTask}
                onUpdateTask={handleUpdateTask}
                highlightedTaskId={highlightedTaskId}
              />
              
              <TasksOverview 
                tasks={groupedTasks.completed}
                type="completed"
                onCompleteTask={handleCompleteTask}
                onDeleteTask={handleDeleteTask}
                onUpdateTask={handleUpdateTask}
                highlightedTaskId={highlightedTaskId}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AddTaskDialog 
        isOpen={isAddTaskDialogOpen}
        onClose={() => setIsAddTaskDialogOpen(false)}
      />
    </div>
  );
};

export default Tasks;

